import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { createStore, useAtomValue, useSetAtom } from 'jotai'
import { Ref, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmationDialog, ConfirmationDialogRef } from '../rapid-ui'
import { AdditionalQuestionsEditor } from './additional-questions'
import { CandidateProfileCard } from './candidate-profile'
import { ConnectorArrow } from './connector-arrow'
import { EditorErrorBoundary } from './error-handling'
import { ExportConfigurator } from './export-configurator'
import { FeedAdTools, FeedAdToolsRef } from './feed-ad-tools'
import { FeedAdSimulator } from './feed-simulator'
import { OuterFormSimulator } from './form-simulator'
import { LandingPageSimulator } from './landing-page-simulator'
import { LandingPageTools, LandingPageToolsRef } from './landing-page-tools'
import { Minimap } from './minimap'
import { OccupationDebugPanel } from './occupation-debug-panel'
import { ReachOverrideEditor } from './reach-override-editor'
import { ScreenHeader } from './screen-header'
import { EditorUserIsViewing, editorUserIsViewingAtom, formAtom, thankYouScreensAtom } from './state'
import { StoryAdTools, StoryAdToolsRef } from './story-ad-tools'
import { StoryAdSimulator } from './story-simulator'
import { ThankYouPageEditor, ThankYouPageEditorRef } from './thank-you-page-editor'

const SIMULATOR_SECTION_TARGETS: Array<{
	id: string
	section: EditorUserIsViewing
}> = [
	{ id: 'resource-feed-ads', section: 'feed' },
	{ id: 'resource-story-ads', section: 'story' },
	{ id: 'resource-landing-page', section: 'landing' },
	{ id: 'resource-form', section: 'form' },
	{ id: 'resource-thank-you-page', section: 'thank-you' },
	{ id: 'resource-export', section: 'export' },
]

export type EditorEditSimulatorsRef = {
	renderFeedAd: () => Promise<Blob>
	renderStoryAd: () => Promise<Blob>
}

export type EditorEditSimulatorsProps = {
	ref?: Ref<EditorEditSimulatorsRef>
	thankYouStore?: ReturnType<typeof createStore>
	campaignId?: string
}

export function EditorEditSimulators({ ref, thankYouStore, campaignId }: EditorEditSimulatorsProps) {
	const { t } = useTranslation('editor')
	const feedAdToolsRef = useRef<FeedAdToolsRef>(null)
	const storyAdToolsRef = useRef<StoryAdToolsRef>(null)
	const landingPageToolsRef = useRef<LandingPageToolsRef>(null)
	const thankYouPageRef = useRef<ThankYouPageEditorRef>(null)
	const thankYouResetConfirmDialogRef = useRef<ConfirmationDialogRef>(null)
	const thankYouResetAllConfirmDialogRef = useRef<ConfirmationDialogRef>(null)
	const form = useAtomValue(formAtom)
	const setUserIsViewing = useSetAtom(editorUserIsViewingAtom)

	const thankYouScreens = useAtomValue(thankYouScreensAtom, { store: thankYouStore })
	const hasThankYouScreens = useMemo(() => (thankYouScreens?.length ?? 0) > 0, [thankYouScreens?.length])

	const handleThankYouScreenReset = async () => {
		try {
			await thankYouResetConfirmDialogRef.current?.confirm({
				title: t('actions.resetToDefault'),
				message: t('actions.resetToDefaultMessage'),
				confirmText: t('actions.reset'),
				cancelText: t('actions.cancel'),
			})
			thankYouPageRef.current?.resetToDefault()
		} catch {
			// User cancelled, do nothing
		}
	}

	const handleThankYouScreenResetAll = async () => {
		try {
			await thankYouResetAllConfirmDialogRef.current?.confirm({
				title: t('actions.resetAllToDefault'),
				message: t('actions.resetAllToDefaultMessage'),
				confirmText: t('actions.resetAll'),
				cancelText: t('actions.cancel'),
			})
			thankYouPageRef.current?.resetAllToDefault()
		} catch {
			// User cancelled, do nothing
		}
	}

	useImperativeHandle(ref, () => ({
		renderFeedAd() {
			return feedAdToolsRef.current?.render() ?? Promise.reject(new Error('No feed ad tools ref'))
		},
		renderStoryAd() {
			return storyAdToolsRef.current?.render() ?? Promise.reject(new Error('No story ad tools ref'))
		},
	}), [])

	const organisationId = useJobillaOrgId()
	const lastTrackedSectionRef = useRef<EditorUserIsViewing | null>(null)

	useEffect(() => {
		lastTrackedSectionRef.current = null
	}, [campaignId, organisationId])

	// Determine what the user is viewing based on scroll position
	useEffect(() => {
		const scrollContainer = document.querySelector<HTMLElement>(
			'[data-editor-scroll-container="true"]',
		)

		let frame = 0

		const getScrollPosition = () => {
			if (scrollContainer) {
				return scrollContainer.scrollTop + scrollContainer.clientHeight / 2
			}

			return window.scrollY + window.innerHeight / 2
		}

		const getElementOffset = (element: HTMLElement) => {
			if (scrollContainer) {
				const containerRect = scrollContainer.getBoundingClientRect()
				return (
					element.getBoundingClientRect().top
					- containerRect.top
					+ scrollContainer.scrollTop
				)
			}

			return element.getBoundingClientRect().top + window.scrollY
		}

		const updateViewingSection = (section: EditorUserIsViewing) => {
			setUserIsViewing(section)
			if (section !== lastTrackedSectionRef.current) {
				lastTrackedSectionRef.current = section
				logger.track('Editor: Simulator section viewed', {
					data: {
						section,
						campaign_id: campaignId,
						organisation_id: organisationId,
					},
				})
			}
		}

		const updateVisibleSection = () => {
			const scrollPosition = getScrollPosition()
			const sections = SIMULATOR_SECTION_TARGETS.map((target) => {
				const element = document.getElementById(target.id)
				if (!(element instanceof HTMLElement)) {
					return null
				}

				return {
					section: target.section,
					offsetTop: getElementOffset(element),
				}
			}).filter(
				(
					section,
				): section is { section: EditorUserIsViewing; offsetTop: number } => section !== null,
			)

			if (sections.length === 0) {
				updateViewingSection('feed')
				return
			}

			for (let index = 0; index < sections.length; index += 1) {
				const current = sections[index]
				const next = sections[index + 1]
				const nextOffset = next?.offsetTop ?? Number.POSITIVE_INFINITY

				if (
					scrollPosition >= current.offsetTop
					&& scrollPosition < nextOffset
				) {
					updateViewingSection(current.section)
					return
				}
			}

			updateViewingSection(sections[sections.length - 1]?.section ?? 'feed')
		}

		const scheduleUpdate = () => {
			if (frame) {
				cancelAnimationFrame(frame)
			}
			frame = window.requestAnimationFrame(updateVisibleSection)
		}

		const target = scrollContainer ?? window
		target.addEventListener('scroll', scheduleUpdate, { passive: true })
		window.addEventListener('resize', scheduleUpdate)
		scheduleUpdate()

		return () => {
			target.removeEventListener('scroll', scheduleUpdate)
			window.removeEventListener('resize', scheduleUpdate)
			if (frame) {
				cancelAnimationFrame(frame)
			}
		}
	}, [setUserIsViewing, campaignId, organisationId])

	return (
		<div className="flex flex-col w-full items-center py-20 pr-20 gap-24 relative">
			<ConnectorArrow
				fromSelector="[data-additional-questions-hint]"
				toSelector="#resource-additional-questions"
			/>
			<div className="fixed right-0 w-[max-content] top-1/2 -translate-y-1/2">
				<Minimap thankYouStore={thankYouStore} campaignId={campaignId} />
			</div>

			<div id="resource-feed-ads">
				<ScreenHeader title={t('screenHeaders.feedAd')} />
				<EditorErrorBoundary debugId="feed-ad">
					<FeedAdTools ref={feedAdToolsRef}>
						<FeedAdSimulator />
					</FeedAdTools>
				</EditorErrorBoundary>
			</div>
			<div id="resource-story-ads">
				<ScreenHeader title={t('screenHeaders.storyAd')} />
				<EditorErrorBoundary debugId="story-ad">
					<StoryAdTools ref={storyAdToolsRef}>
						<StoryAdSimulator />
					</StoryAdTools>
				</EditorErrorBoundary>
			</div>
			<EditorErrorBoundary debugId="reach-override">
				<ReachOverrideEditor />
			</EditorErrorBoundary>
			<div id="resource-landing-page">
				<ScreenHeader
					title={t('screenHeaders.landingPage')}
					tools={
						<Button
							type={'subtle'}
							size={'tiny'}
							postfix={'popout'}
							onClick={() => {
								const url = new URL(import.meta.env.VITE_FORM_URL + form?.id + '?reference')
								window.open(url.toString(), '_blank', 'noopener,noreferrer')
							}}
						>
							{t('screenHeaders.openPreview')}
						</Button>
					}
				/>
				<EditorErrorBoundary debugId="landing-page">
					<LandingPageTools ref={landingPageToolsRef}>
						<LandingPageSimulator />
					</LandingPageTools>
				</EditorErrorBoundary>
			</div>
			<div id="resource-form">
				<ScreenHeader
					title={t('screenHeaders.form')}
					tools={
						<Button
							type={'subtle'}
							size={'tiny'}
							postfix={'popout'}
							onClick={() => {
								const url = new URL(import.meta.env.VITE_FORM_URL + form?.id + '/respond?reference')
								window.open(url.toString(), '_blank', 'noopener,noreferrer')
							}}
						>
							{t('screenHeaders.openPreview')}
						</Button>
					}
				/>
				<EditorErrorBoundary debugId="form">
					<OuterFormSimulator />
				</EditorErrorBoundary>
			</div>
			<div id="resource-thank-you-page">
				<ScreenHeader
					title={t('screenHeaders.thankYouPage')}
					tools={
						<>
							{hasThankYouScreens && (
								<Button
									type={'subtle'}
									size={'tiny'}
									onClick={handleThankYouScreenReset}
								>
									{t('actions.reset')}
								</Button>
							)}
							<Button
								type={'destructive'}
								size={'tiny'}
								onClick={handleThankYouScreenResetAll}
							>
								{t('actions.resetAll')}
							</Button>
						</>
					}
				/>
				<EditorErrorBoundary debugId="thank-you-page">
					<ThankYouPageEditor ref={thankYouPageRef} store={thankYouStore} />
				</EditorErrorBoundary>
			</div>
			<div id={'resource-additional-questions'}>
				{campaignId && (
					<>
						<ScreenHeader
							title={t('screenHeaders.additionalQuestions')}
						/>
						<EditorErrorBoundary debugId="additional-questions">
							<AdditionalQuestionsEditor campaignId={campaignId} />
						</EditorErrorBoundary>
					</>
				)}
			</div>
			<div id="resource-candidate-profile">
				{campaignId && (
					<>
						<ScreenHeader title={t('screenHeaders.candidateProfile')} />
						<EditorErrorBoundary debugId="candidate-profile">
							<CandidateProfileCard />
						</EditorErrorBoundary>
					</>
				)}
			</div>
			<div id="resource-export">
				<ScreenHeader title={t('screenHeaders.exportConfig')} />
				<EditorErrorBoundary debugId="export-config">
					<ExportConfigurator isDrafting />
				</EditorErrorBoundary>
			</div>
			<EditorErrorBoundary debugId="occupation-debug">
				<OccupationDebugPanel campaignId={campaignId} />
			</EditorErrorBoundary>
			<ConfirmationDialog ref={thankYouResetConfirmDialogRef} />
			<ConfirmationDialog ref={thankYouResetAllConfirmDialogRef} />
		</div>
	)
}
